import { Suspense,useMemo,useRef } from 'react'
import { useGLTF } from '@react-three/drei'
import { useFrame } from '@react-three/fiber'
import * as THREE from 'three'
import type { ArenaDef } from './content'
import type { WorldCompositionPiece } from './worldExpansionTypes'
import { WORLD_COMPOSITIONS,WORLD_LIBRARY_URLS } from './generatedWorldExpansion'
import { AssetBoundary } from './AssetBoundary'

function prepareWorldAsset(scene:THREE.Object3D,piece:WorldCompositionPiece){
 const model=scene.clone(true)
 const box=new THREE.Box3().setFromObject(model),size=box.getSize(new THREE.Vector3())
 const scale=size.y>0?piece.height/size.y:1
 model.scale.setScalar(scale)
 const fitted=new THREE.Box3().setFromObject(model),center=fitted.getCenter(new THREE.Vector3())
 model.position.set(-center.x,-fitted.min.y,-center.z)
 model.traverse(node=>{
  if(!(node instanceof THREE.Mesh))return
  node.castShadow=piece.motion==='static'
  node.receiveShadow=piece.motion==='static'
  if(node.material instanceof THREE.MeshStandardMaterial){
   const material=node.material.clone()
   material.envMapIntensity=Math.max(1.15,material.envMapIntensity)
   material.roughness=Math.max(.22,Math.min(.82,material.roughness))
   if(piece.accent){material.emissive=new THREE.Color(piece.accent);material.emissiveIntensity=.08}
   node.material=material
  }
 })
 return model
}

function WorldPiece({piece,index}:{piece:WorldCompositionPiece;index:number}){
 const url=WORLD_LIBRARY_URLS[piece.asset]
 const {scene}=useGLTF(url),root=useRef<THREE.Group>(null)
 const model=useMemo(()=>prepareWorldAsset(scene,piece),[scene,piece])
 useFrame(({clock},dt)=>{
  if(!root.current||piece.motion==='static')return
  const t=clock.elapsedTime+index*1.73,baseY=piece.position[1]
  root.current.position.y=THREE.MathUtils.damp(root.current.position.y,baseY+Math.sin(t*(piece.motion==='fly'?.36:.62))*(piece.motion==='fly'?.8:.24),2.8,dt)
  root.current.rotation.y=piece.rotationY+Math.sin(t*.18)*(piece.motion==='fly'?.22:.08)
  if(piece.motion==='fly')root.current.position.x=piece.position[0]+Math.sin(t*.13)*2.4
 })
 return <group ref={root} position={piece.position} rotation={[0,piece.rotationY,0]}><primitive object={model}/>{piece.motion!=='static'&&piece.accent&&<pointLight color={piece.accent} intensity={3.4} distance={8}/>}</group>
}

export function ScenarioWorldComposition({arena}:{arena:ArenaDef}){
 const composition=WORLD_COMPOSITIONS[arena.id]
 if(!composition)return null
 return <group>{composition.pieces.map((piece,index)=><AssetBoundary key={piece.id} label={'world composition '+piece.id}><Suspense fallback={null}><WorldPiece piece={piece} index={index}/></Suspense></AssetBoundary>)}</group>
}
